import React, { useEffect, useState } from 'react';
import { isMobile } from '@/biz/tool';
import { OrderStatus } from '@/interface/order';
import { useTranslation } from 'react-i18next';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';

interface Props {
  order: any;
  bgImg?: string;
  title?: string;
  isOffering?: boolean;
}

// 订单奖金 提示
const BonusTip: React.FC<Props> = ({
  order,
  bgImg = '/bonus-tip-bg.png',
  title,
  isOffering = false
}) => {
  const { t } = useTranslation();
  const [isComplete, setIsComplete] = useState(false);
  const mobile = isMobile();

  useEffect(() => {
    if (
      [
        OrderStatus.PendingPayment,
        OrderStatus.PaymentSubmitted,
        OrderStatus.Paid
      ].includes(order?.status)
    ) {
      setIsComplete(true);
    }
  }, [order?.status]);
  const { selectedPriceFields, competitivePriceDetail } =
    useMultiplePricesStore();
  const offeringBonusAmount =
    competitivePriceDetail?.bonusAmount ??
    selectedPriceFields?.[0]?.bonusAmount;
  const style = {
    '--digits': String(
      isOffering ? offeringBonusAmount : selectedPriceFields?.[0]?.bonusAmount
    ).length
  } as React.CSSProperties;
  const isShortBanner = bgImg.includes('bg2');

  const renderShortBanner = (
    amount: number | string,
    text: string,
    amountStyle: React.CSSProperties
  ) => (
    <div className="relative w-full overflow-hidden rounded-lg">
      <img
        src={bgImg}
        alt="bonus-tip-icon"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        className={`relative z-10 flex w-full items-center font-semibold ${
          mobile ? 'gap-1 px-2 py-2' : 'gap-2 px-[14px] py-2'
        }`}
      >
        <div
          className={`flex-shrink-0 whitespace-nowrap text-center tabular-nums leading-none bg-gradient-to-b from-[#7047EB] to-[#D6B7FA] bg-clip-text text-transparent ${
            mobile
              ? 'text-[clamp(12px,calc(80px/var(--digits)*1.05),24px)]'
              : 'text-[clamp(18px,calc(128px/var(--digits)*1.05),36px)]'
          }`}
          style={amountStyle}
        >
          ${amount}
        </div>
        <div
          className={`min-w-0 flex-1 font-bold text-[#330E81] ${
            mobile ? 'break-words text-xs leading-[18px]' : 'text-lg leading-6'
          }`}
        >
          {text}
        </div>
      </div>
    </div>
  );

  if ([OrderStatus.Invited].includes(order?.status)) {
    const bonusAmount = isOffering
      ? offeringBonusAmount
      : selectedPriceFields?.[0]?.bonusAmount;

    if (Number(bonusAmount) <= 0 || Number.isNaN(Number(bonusAmount))) {
      return null;
    }

    if (!!order?.ahaPicksOffer) {
      const invitedText = 'Earn an extra bonus for delivering on time!';
      if (isShortBanner) {
        return renderShortBanner(bonusAmount ?? '', invitedText, style);
      }
      return (
        <div className="relative w-full rounded-lg">
          <div className="absolute bottom-0 left-0 z-10 mx-auto flex w-full items-center gap-2 px-[14px] font-semibold h-[58px]">
            <div
              className="flex-shrink-0 whitespace-nowrap text-center tabular-nums bg-gradient-to-b from-[#7047EB] to-[#D6B7FA] bg-clip-text text-transparent text-[clamp(18px,calc(128px/var(--digits)*1.05),36px)]"
              style={style}
            >
              ${bonusAmount}
            </div>
            <div className="min-w-0 flex-1 text-base font-bold leading-5 text-[#330E81]">
              {invitedText}
            </div>
          </div>
          <img src={bgImg} alt="bonus-tip-icon" className="h-auto w-full" />
        </div>
      );
    } else if (isOffering) {
      // 接单流程中的展示
      const invitedText = 'Earn an extra bonus for delivering on time!';
      if (isShortBanner) {
        return renderShortBanner(bonusAmount ?? '', invitedText, style);
      }
      return (
        <div className="relative w-full rounded-lg">
          <div className="absolute bottom-0 left-0 z-10 mx-auto flex w-full items-center gap-2 px-[14px] font-semibold h-[58px]">
            <div
              className="flex-shrink-0 whitespace-nowrap text-center tabular-nums bg-gradient-to-b from-[#7047EB] to-[#D6B7FA] bg-clip-text text-transparent text-[clamp(18px,calc(128px/var(--digits)*1.05),36px)]"
              style={style}
            >
              ${bonusAmount}
            </div>
            <div className="min-w-0 flex-1 text-base font-bold leading-5 text-[#330E81]">
              {invitedText}
            </div>
          </div>
          <img src={bgImg} alt="bonus-tip-icon" className="h-auto w-full" />
        </div>
      );
    }
    return (
      <div className="w-full rounded-lg">
        <img
          src="/bonus-tip-bg-2.png"
          alt="bonus-tip-icon"
          className="w-full"
        />
      </div>
    );
  }

  const select =
    order?.status === OrderStatus.CreatorApplied
      ? order?.priceNegotiationField || {}
      : order?.interestedPriceFields?.[0] || {};
  if (
    [OrderStatus.Cancelled].includes(order?.status) ||
    !select?.bonusAmount ||
    select?.bonusAmount <= 0
  ) {
    return null;
  }

  const textContent =
    title ||
    (isComplete
      ? t('modal.bonus_tip.complete', {
          defaultValue: 'Nailed it. On-time bonus earned!'
        })
      : t('modal.bonus_tip.incomplete', {
          defaultValue: 'Earn an extra bonus for delivering on time!'
        }));

  const amountStyle = {
    '--digits': String(select?.bonusAmount).length
  } as React.CSSProperties;

  if (isShortBanner) {
    return renderShortBanner(select?.bonusAmount, textContent, amountStyle);
  }

  if (mobile) {
    return (
      <div
        className="relative w-full overflow-hidden rounded-lg"
        style={{ aspectRatio: '858 / 180' }}
      >
        <img
          src={bgImg}
          alt="bonus-tip-icon"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute bottom-0 left-0 z-10 flex w-[calc(100%-16px)] items-center gap-1 px-2 font-semibold">
          <div
            className="flex-shrink-0 whitespace-nowrap text-center tabular-nums bg-gradient-to-b from-[#7047EB] to-[#D6B7FA] bg-clip-text text-transparent text-[clamp(12px,calc(80px/var(--digits)*1.05),24px)] leading-[40px]"
            style={amountStyle}
          >
            ${select?.bonusAmount}
          </div>
          <div className="min-w-0 flex-1 break-words text-xs font-bold leading-[18px] text-[#330E81]">
            {textContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-lg">
      <div className="absolute bottom-0 left-0 z-10 mx-auto flex w-full items-center gap-2 px-[14px] font-semibold h-[58px]">
        <div
          className="flex-shrink-0 whitespace-nowrap text-center tabular-nums bg-gradient-to-b from-[#7047EB] to-[#D6B7FA] bg-clip-text text-transparent text-[clamp(18px,calc(128px/var(--digits)*1.05),36px)]"
          style={amountStyle}
        >
          ${select?.bonusAmount}
        </div>
        <div className="min-w-0 flex-1 text-base font-bold leading-5 text-[#330E81]">
          {textContent}
        </div>
      </div>
      <img src={bgImg} alt="bonus-tip-icon" className="h-auto w-full" />
    </div>
  );
};

export default BonusTip;
